import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { earlierVisitFailure, offScreenFailure, offScreenSignal } from '../operator/operatorTaskScope';
import { assembleKit, disassembleKit, findKit, scanKitCode } from './operatorKitsThunks';
import { KIT_WORK, kitStaleSignal, kitWriteMessage } from './operatorKitsUtils';

/** What did not happen, said of a write whose kit is off screen. */
const FAILED = { [KIT_WORK.ASSEMBLE]: 'no se armó', [KIT_WORK.DISASSEMBLE]: 'no se desarmó' };

/**
 * A write's band. Off screen it names the kit and says no step to take; a
 * lost answer offers «Reintentar» wherever it shows: the band carries the
 * write, and its key keeps it from registering twice. On screen a 412 says
 * what changed and what to do now, while the kit and its plan load again.
 */
const write = (kind) => ({
  success: ({ payload, meta }) => {
    const signal = kitWriteMessage(kind, payload, meta.arg);
    return meta.offScreenTask ? offScreenSignal(meta.offScreenTask, signal) : signal;
  },
  error: ({ payload, meta }, fallback) => {
    if (meta.earlierVisit) return earlierVisitFailure(payload);
    const name = meta.offScreenTask;
    if (name) return isRetryable(payload) ? offScreenSignal(name, fallback) : offScreenFailure(name, payload, FAILED[kind]);
    return payload?.kind === OPERATOR_ERROR.STALE ? kitStaleSignal(kind, payload, meta.arg) : undefined;
  },
  retry: OPERATOR_FLOW.KITS,
  keyed: true,
});

/**
 * What the band says for each kit outcome; see `operatorListeners`. The
 * reads of the list, the kit and its plan say their failures in place.
 */
export const KITS_SIGNAL_RULES = {
  // One kit opens, and its screen speaks; several are offered. Once the station is off screen, nothing.
  [findKit.typePrefix]: {
    success: ({ payload, meta }) =>
      meta.offScreenTask || payload.kind !== 'choose'
        ? null
        : { tone: 'warning', message: `${payload.code} es de más de un kit: elige cuál.` },
    error: ({ meta }) => (meta.offScreenTask ? null : undefined),
  },
  [scanKitCode.typePrefix]: {
    success: ({ payload, meta }) => {
      if (meta.offScreenTask) return null;
      const { name, code, suggested } = payload.destination;
      return suggested
        ? { tone: 'step', message: `${name || code}: es la sugerida.` }
        : { tone: 'warning', message: `${name || code} no es la sugerida; puedes dejarlos igual.` };
    },
    error: ({ meta }) => (meta.offScreenTask ? null : undefined),
  },
  [assembleKit.typePrefix]: write(KIT_WORK.ASSEMBLE),
  [disassembleKit.typePrefix]: write(KIT_WORK.DISASSEMBLE),
};
